import { ChangeEvent, FormEvent, useState } from "react";
import {
  RadioOption,
  TableTitle,
} from "../../components/mobileSize/TableTitle";
import { GetNokResult, Nok, SystemSetting } from "../../types/types";
import { Box } from "@mui/material";
import { SearchForm } from "../../components/common/SearchForm";
import { Button } from "../../components/common/Button";
import theme from "../../theme";
import { DotLoader } from "../../components/dotLoader";
import Spinner from "../../components/spinner";
import { MobileTable } from "../../components/mobileSize/MobileTable";
import { CustomTooltip } from "../../components/common/CustomTooltip";
import styled from "@emotion/styled";
import { DetailUserIcon, TrashCanIcon } from "../../assets/icons/icons";
import { formatDate } from "../../utils/util";
import { PhoneMaskToggle } from "../../components/PhoneMaskToggle";
import userStorage from "../../utils/userStorage";
import { SelectBoxGroupId } from "../../components/SelectBoxGroupId";
import { FetchParams } from "./manageNok";
import { useI18n } from "../../locales";

type Prop = {
  data?: GetNokResult;
  isLoading: boolean;
  systemSetting: SystemSetting | null;
  onClickBtn: ({ searchKey, groupId }: FetchParams) => void;
  sort?: string;
  setSort?: React.Dispatch<React.SetStateAction<string | undefined>>;
  setOpenRegisterModal: React.Dispatch<React.SetStateAction<boolean>>;
  onClickDeleteIcon: (el: Nok) => void;
  onClickDetailIcon: (el: Nok) => void;
};

export const MobileManageNok = ({
  data,
  isLoading,
  systemSetting,
  onClickBtn,
  sort,
  setSort,
  setOpenRegisterModal,
  onClickDeleteIcon,
  onClickDetailIcon,
}: Prop) => {
  const [openFilter, setOpenFilter] = useState(false);
  const [input, setInput] = useState("");
  const [groupSub, setGroupSub] = useState("all"); // 하위그룹코드(화면용)
  const user = userStorage.get();
  const { t } = useI18n();
  const sortList: RadioOption[] = [
    { label: t.manageNok.defaultSort, value: `` },
    { label: t.manageNok.sortByName, value: `name` },
    { label: t.manageNok.sortByContact, value: `id` },
    { label: t.manageNok.sortByOwnerName, value: `related_name` },
    { label: t.manageNok.sortByOwnerContact, value: `related_id` },
    { label: t.manageNok.sortByRelationship, value: `relation` },
    { label: t.manageNok.sortByCreationTime, value: `registered_at` },
  ];

  const onChange = (event: ChangeEvent<HTMLInputElement>) => {
    setInput(event.currentTarget.value);
  };

  const onSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    onClickBtn({
      searchKey: input,
      groupId: groupSub,
    });
  };
  return (
    <>
      {/* 필터 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          minHeight: openFilter ? "auto" : "0px",
          maxHeight: openFilter ? "100vh" : "0px",
          overflow: openFilter ? "visible" : "hidden",
          gap: "6px",
          paddingBottom: openFilter ? "16px" : "0px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: `8px`,
            alignItems: "center",
          }}
        >
          {/* 하위 그룹 조회 */}
          <SelectBoxGroupId
            groupSub={groupSub}
            setGroupSub={setGroupSub}
            width={140}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            gap: `5px`,
            alignItems: "center",
          }}
        >
          <SearchForm
            onSubmit={onSubmit}
            placeholder={t.manageNok.searchPlaceholder}
            onChange={onChange}
            inputValue={input}
            setInputValue={setInput}
            width="140px"
            gap="5px"
            bottom="0"
            marginR="0"
          />
          <Button
            bgColor={theme.color.aquamarine_500}
            hoverColor={theme.color.aquamarine_600}
            height="30px"
            isDisabled={!systemSetting?.create}
            width="95px"
            onClick={() => setOpenRegisterModal(true)}
            paddingX="0px"
          >{t.manageNok.registerRecipient}</Button>
        </Box>
      </Box>

      {/* 테이블 타이틀 섹션 */}
      <TableTitle
        title={
          isLoading ? (
            <div style={{ height: "15px" }}>
              <DotLoader height="6px" />
            </div>
          ) : (
            `${t.manageNok.recipientInfo} - ${data?.total_items ?? 0} ${t.common.casesUnit}`
          )
        }
        sort={sort}
        setSort={setSort}
        setOpenFilter={setOpenFilter}
        sortList={sortList}
      />

      {/* 테이블 섹션 */}
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "4px",
          flexGrow: 1,
        }}
      >
        {isLoading && <Spinner />}
        {data &&
          data.list.length > 0 &&
          data.list.map((el) => (
            <MobileTable
              key={el.seq.toString()}
              defaultInfoGap="0px"
              defaultInfo={
                <>
                  <TextWrapper>
                    {/* 추가 알림 수신자 이름 */}
                    <CustomTooltip title={el?.name} active>
                      <Text $width="49%">{el.name}</Text>
                    </CustomTooltip>
                    {/* 추가 알림 수신자연락처 */}
                    <Text $width="49%">
                      <PhoneMaskToggle
                        text={el.id}
                        who={2} // 추가 알림 수신자
                        userIdx={user?.idx}
                        name={el.name}
                        isMobile
                        justifyContent="flex-end"
                      />
                      {/* {el.id ? el.id : "추가 알림 수신자 연락처 없음"} */}
                    </Text>
                  </TextWrapper>

                  <TextWrapper>
                    {/* 소유자 이름 */}
                    <CustomTooltip
                      title={el?.related_name}
                      active={!!el.related_name}
                    >
                      <Text $width="49%">
                        {el.related_name ? el.related_name : "소유자 없음"}
                      </Text>
                    </CustomTooltip>
                    {/* 소유자연락처 */}
                    <Text $width="49%">
                      <PhoneMaskToggle
                        text={el.related_id}
                        who={1} // 소유자
                        userIdx={user?.idx}
                        name={el.related_name}
                        isMobile
                        justifyContent="flex-end"
                      />
                      {/* {el.related_id
                        ? el.related_id
                        : "소유자 연락처 없음"} */}
                    </Text>
                  </TextWrapper>

                  <TextWrapper>
                    {/* 관계 */}
                    <CustomTooltip
                      title={el?.relation}
                      active={!!el.relation}
                    >
                      <Text $width="49%">
                        {el.relation ? el.relation : "관계 없음"}
                      </Text>
                    </CustomTooltip>
                    {/* 계정생성일시 */}
                    <Text $width="49%" style={{ textAlign: "right" }}>
                      {formatDate(el.registered_at, "yyyy-MM-dd HH:mm")}
                    </Text>
                  </TextWrapper>
                </>
              }
              detailInfo={
                <DetailInfoWrapper>
                  <IconsWrapper>
                    {/* 정보 상세 보기 */}
                    <IconWrapper onClick={() => onClickDetailIcon(el)}>
                      <DetailUserIcon />
                    </IconWrapper>

                    {/* 계정 삭제하기 */}
                    <IconWrapper
                      onClick={() => {
                        if (!systemSetting?.delete) return;
                        onClickDeleteIcon(el);
                      }}
                    >
                      <TrashCanIcon />
                    </IconWrapper>
                  </IconsWrapper>
                </DetailInfoWrapper>
              }
            />
          ))}
        {!isLoading && data && data.list.length === 0 && (
          <NoDataMassage>조회된 데이터가 없습니다.</NoDataMassage>
        )}
      </Box>
    </>
  );
};

const TextWrapper = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const Text = styled.div<{ $fontSize?: string; $width?: string }>`
  flex: ${({ $width }) => ($width ? `0 1 ${$width}` : "1")};
  width: ${({ $width }) => ($width ? $width : "calc(100% - 32px)")};
  max-width: ${({ $width }) =>
    $width ? $width : "calc(100vw- 32px- 32px)"};
  font-size: ${({ $fontSize }) =>
    $fontSize ? $fontSize : theme.fontSize.FONT_2};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DetailInfoWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: end;
`;

const IconsWrapper = styled.div`
  display: flex;
  justify-content: end;
  align-items: center;
  gap: 16px;
`;

const IconWrapper = styled.button<{
  $wrapperWH?: string;
  $iconWH?: string;
}>`
  width: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `28px`)};
  height: ${({ $wrapperWH }) => ($wrapperWH ? $wrapperWH : `28px`)};
  border-radius: 50%;
  background: ${theme.color.neutral_200};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: ${({ $iconWH }) => ($iconWH ? $iconWH : `14px`)};
    height: ${({ $iconWH }) => ($iconWH ? $iconWH : `14px`)};
  }
`;

const NoDataMassage = styled.div`
  text-align: center;
  color: ${theme.color.neutral_500};
  padding-top: 200px;
`;
